import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'

import { cn } from '@/lib/utils'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const INTENTS = ['a print', 'a commission', 'something else']

const EASE = 'ease-[cubic-bezier(0.16,1,0.3,1)]'

type Status = 'idle' | 'sending' | 'sent'

const labelClass = 'rev-label block text-[color:var(--rev-ink-faint)]'
const labelStyle = { fontSize: '11.5px', letterSpacing: '0.26em' }
const hintClass = 'rev-note mt-[8px] block text-[color:var(--rev-ink-faint)]'
const inputClass =
    'font-display peer w-full bg-transparent pb-[11px] text-[color:var(--rev-ink)] caret-[color:var(--rev-ink)] outline-none placeholder:text-[color:var(--rev-ink-faint)] placeholder:opacity-60'
const inputStyle = {
    fontSize: 'clamp(21px, 2.6vw, 32px)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    lineHeight: 1.25
}

interface FieldProps {
    id: string
    label: string
    hint: string
    children: ReactNode
}

/** label, hint, then a rule that draws itself across on focus */
const Field: FC<FieldProps> = ({ id, label, hint, children }): ReactNode => (
    <div className='pt-[clamp(28px,4.5vh,48px)] first:pt-0'>
        <label htmlFor={id} className={labelClass} style={labelStyle}>
            {label}
        </label>

        <span className={hintClass}>{hint}</span>

        <div className='relative mt-[12px]'>
            {children}

            <span aria-hidden='true' className='absolute bottom-0 left-0 h-px w-full bg-[var(--rev-hairline)]' />

            <span
                aria-hidden='true'
                className={cn(
                    'absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-[var(--rev-ink)] transition-transform duration-700 peer-focus:scale-x-100',
                    EASE
                )}
            />
        </div>
    </div>
)

export const ContactForm: FC = (): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [intent, setIntent] = useState('')
    const [message, setMessage] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')
    const [mark, setMark] = useState('')

    const reset = (): void => {
        setName('')
        setEmail('')
        setIntent('')
        setMessage('')
        setError('')
        setMark('')
        setStatus('idle')
    }

    const problemWith = (): string => {
        if (name.trim().length < 2) return 'Leave a name we can answer to.'
        if (!EMAIL.test(email.trim())) return 'That address will not reach you.'
        if (message.trim().length < 10) return 'Say a little more than that.'

        return ''
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') return

        const problem = problemWith()

        if (problem) {
            setError(problem)

            return
        }

        setError('')
        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, intent, message })
            })

            const data = (await response.json().catch(() => ({}))) as {
                error?: string
                mark?: string
            }

            if (!response.ok) {
                setError(data.error ?? 'That did not go through. Try once more.')
                setStatus('idle')

                return
            }

            setMark(data.mark ?? '')
            setStatus('sent')
        } catch {
            setError('No line out. Check the connection and try again.')
            setStatus('idle')
        }
    }

    if (status === 'sent') {
        return (
            <div role='status' aria-live='polite' className='pt-[clamp(20px,5vh,50px)]'>
                <span className={labelClass} style={labelStyle}>
                    Received
                </span>

                <p
                    className='font-editorial mt-[22px] max-w-[24ch] text-[color:var(--rev-ink)]'
                    style={{ fontSize: 'clamp(24px, 3.4vw, 40px)', lineHeight: 1.25 }}
                >
                    We will write back before the grain settles.
                </p>

                <div aria-hidden='true' className='mt-[clamp(30px,5vh,46px)] h-px w-full bg-[var(--rev-hairline)]' />

                <div className='mt-[16px] flex flex-wrap items-center justify-between gap-[14px]'>
                    <span className='rev-mark text-[color:var(--rev-ink-faint)]'>
                        {mark ? `Filed as № ${mark}` : 'Filed in the archive'}
                    </span>

                    <button
                        type='button'
                        onClick={reset}
                        className='rev-mark group inline-flex items-center gap-[10px] text-[color:var(--rev-ink-dim)] transition-opacity duration-500 hover:opacity-100'
                    >
                        Write another

                        <span
                            aria-hidden='true'
                            className={cn(
                                'h-px w-[26px] bg-[var(--rev-ink)] opacity-40 transition-all duration-700 group-hover:w-[44px] group-hover:opacity-100',
                                EASE
                            )}
                        />
                    </button>
                </div>
            </div>
        )
    }

    return (
        <form onSubmit={onSubmit} noValidate className='flex flex-col'>
            <Field id='rev-name' label='Your name' hint='the one you answer to'>
                <input
                    id='rev-name'
                    name='name'
                    type='text'
                    autoComplete='name'
                    maxLength={120}
                    value={name}
                    onChange={event => setName(event.target.value)}
                    placeholder='—'
                    className={inputClass}
                    style={inputStyle}
                />
            </Field>

            <Field id='rev-email' label='Your email' hint='where we write back'>
                <input
                    id='rev-email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    maxLength={200}
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    placeholder='—'
                    className={inputClass}
                    style={inputStyle}
                />
            </Field>

            <fieldset className='m-0 border-0 p-0 pt-[clamp(28px,4.5vh,48px)]'>
                <legend className={labelClass} style={labelStyle}>
                    What is this about
                </legend>

                <span className={hintClass}>optional</span>

                <div className='mt-[16px] flex flex-wrap gap-x-[clamp(18px,3vw,34px)] gap-y-[14px]'>
                    {INTENTS.map(value => (
                        <label key={value} className='group relative inline-block cursor-pointer pb-[7px]'>
                            <input
                                type='radio'
                                name='intent'
                                value={value}
                                checked={intent === value}
                                onChange={() => setIntent(value)}
                                className='peer sr-only'
                            />

                            <span
                                className='font-editorial text-[color:var(--rev-ink-dim)] transition-colors duration-500 peer-checked:text-[color:var(--rev-ink)]'
                                style={{ fontSize: 'clamp(18px, 2.2vw, 25px)' }}
                            >
                                {value}
                            </span>

                            <span
                                aria-hidden='true'
                                className={cn(
                                    'absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-[var(--rev-ink)] transition-transform duration-500 group-hover:scale-x-100 peer-checked:scale-x-100 peer-focus-visible:scale-x-100',
                                    EASE
                                )}
                            />
                        </label>
                    ))}
                </div>
            </fieldset>

            <Field id='rev-note' label='Your message' hint='as long as you like'>
                <textarea
                    id='rev-note'
                    name='message'
                    rows={3}
                    maxLength={4000}
                    value={message}
                    onChange={event => setMessage(event.target.value)}
                    placeholder='—'
                    className={cn(inputClass, 'field-sizing-content min-h-[3.4em] resize-none')}
                    style={inputStyle}
                />
            </Field>

            <div className='mt-[clamp(34px,5vh,56px)] flex flex-wrap items-center gap-x-[26px] gap-y-[16px]'>
                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className={cn(
                        'group inline-flex items-center gap-[16px] border border-[var(--rev-hairline)] px-[clamp(22px,2.4vw,34px)] py-[clamp(14px,1.6vh,18px)] transition-colors duration-500 hover:border-[color:var(--rev-ink)] disabled:opacity-60',
                        EASE
                    )}
                >
                    <span className='rev-eyebrow text-[color:var(--rev-ink)]'>
                        {status === 'sending' ? 'Sending' : 'Send message'}
                    </span>

                    <span
                        aria-hidden='true'
                        className={cn(
                            'h-px w-[22px] bg-[var(--rev-ink)] opacity-50 transition-all duration-500 group-hover:w-[36px] group-hover:opacity-100',
                            EASE,
                            status === 'sending' && 'animate-pulse opacity-100'
                        )}
                    />
                </button>

                <p
                    role='alert'
                    aria-live='polite'
                    className={cn(
                        'rev-note text-[color:var(--rev-ink)] transition-opacity duration-500',
                        error ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    {error || ' '}
                </p>
            </div>
        </form>
    )
}
